import type { RevenueRecord } from './apply-point-in-time';

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function daysBetweenInclusive(start: Date, end: Date): number {
  const startDay = startOfDay(start).getTime();
  const endDay = startOfDay(end).getTime();
  return Math.max(0, Math.floor((endDay - startDay) / MS_PER_DAY) + 1);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Distribute `totalAmount` across the month range [startDate, endDate],
 * weighted by the share of each month covered by the range. Each month's
 * portion is emitted as a `RevenueRecord` dated on the month-end (matches
 * the convention used by the per-month revenue records emitted from the
 * calculators).
 *
 * Idempotent w.r.t. re-applies that change the date range: the caller
 * supplies the same `totalAmount` (captured on dialog open as the session
 * total — equivalent to the demo's `_originalTotalRevenue` cache).
 */
export function applyStraightLine(
  startDate: Date,
  endDate: Date,
  totalAmount: number,
): RevenueRecord[] {
  if (endDate < startDate || totalAmount === 0) {
    return [];
  }

  type Weighted = { monthEnd: Date; weight: number };
  const weighted: Weighted[] = [];
  let totalWeight = 0;

  const cursor = startOfMonth(startDate);
  const lastMonthStart = startOfMonth(endDate);

  while (cursor.getTime() <= lastMonthStart.getTime()) {
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const daysInMonth = daysBetweenInclusive(monthStart, monthEnd);

    const effectiveStart = startDate > monthStart ? startDate : monthStart;
    const effectiveEnd = endDate < monthEnd ? endDate : monthEnd;
    const days = daysBetweenInclusive(effectiveStart, effectiveEnd);

    const weight = daysInMonth > 0 ? days / daysInMonth : 0;
    totalWeight += weight;
    weighted.push({ monthEnd, weight });

    cursor.setMonth(cursor.getMonth() + 1);
  }

  if (totalWeight === 0) {
    return [];
  }

  return weighted
    .map(({ monthEnd, weight }) => ({
      date: formatIsoDate(monthEnd),
      value: round((weight / totalWeight) * totalAmount),
    }))
    .filter((record) => record.value !== 0);
}
